import { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import { format, addMonths } from 'date-fns';

import { createReminder } from 'api/reminder';
import { getAllCalendlyEvents } from 'api/calendly_events';

import { ReactComponent as CloseStrokeSVG } from 'icons/close-stroke.svg';
import Select from 'components/select/select';
import Button from 'components/button';

import { REMINDER_DATE_OPTIONS } from 'config/reminder';

import { useOutsideClick } from 'helpers/clickOutside';

const ReminderModal = ({ onClose, onClickOutside, customerId }) => {
  const modalRef = useRef(null);

  const [event, setEvent] = useState(null);
  const [remiderDate, setReminderDate] = useState(
    REMINDER_DATE_OPTIONS.find((date) => date.default),
  );
  const [loading, setLoading] = useState(false);

  const [validation, setValidation] = useState({
    event: true,
    remiderDate: true,
  });

  const [calendlyEvents, setCalendlyEvents] = useState([]);

  useEffect(() => {
    (async () => {
      const calendlyEvents = await getAllCalendlyEvents();
      const mapedCalendlyEvents = calendlyEvents
        .map((e) => ({
          id: e.id,
          value: e.name,
          label: e.name,
        }))
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });

      setCalendlyEvents(mapedCalendlyEvents);
    })();
  }, []);

  const date = addMonths(new Date(), remiderDate.value);
  const formatedReminderDate = format(date, 'dd/MM/yyyy');

  useOutsideClick(modalRef, onClickOutside);

  const checkValidity = useCallback(() => {
    const validation = {
      event: !!event,
      remiderDate: !!remiderDate,
    };

    setValidation(validation);

    return Object.values(validation).every(Boolean);
  }, [event, remiderDate]);

  const onEventSelectChange = (selectedOption) => {
    setEvent(selectedOption);
  };

  const onReminderDateSelectChange = (selectedOption) => {
    setReminderDate(selectedOption);
  };

  const handleSubmit = async () => {
    if (checkValidity()) {
      const data = {
        customer: {
          id: Number(customerId),
        },
        calendlyEvent: {
          id: Number(event.id),
        },
        reminderDate: date,
      };
      setLoading(true);
      await createReminder(data);
      setLoading(false);
      onClose();
    }
  };

  return (
    <div
      className="fullscreen-modal-container"
      style={{ visibility: 'visible' }}
    >
      <div className="fullscreen-modal-background"></div>
      <div className="fullscreen-modal" ref={modalRef}>
        <section className="modal-content">
          <header className="modal-header">
            <h1>Reminder for patient to book an appointment</h1>
          </header>
          <span onClick={onClose} className="pointer close">
            <CloseStrokeSVG />
          </span>

          <div className="modal-body ">
            <p className="mb6">
              {' '}
              A reminder will be sent to the patient via SMS and email on{' '}
              <b>{formatedReminderDate}</b>.
            </p>
            <div className="flex flex-column items-center pt8">
              <div className="w-100 flex flex-row items-start items-center">
                <Select
                  valid={validation.event}
                  validationText="This question is required!"
                  isMulti={false}
                  label="Event type*"
                  value={event}
                  options={calendlyEvents}
                  onChange={onEventSelectChange}
                />
              </div>
              <div className="w-100 flex flex-row items-start items-center">
                <Select
                  valid={validation.remiderDate}
                  validationText="This question is required!"
                  isMulti={false}
                  label="Set reminder in*"
                  value={remiderDate}
                  options={REMINDER_DATE_OPTIONS}
                  onChange={onReminderDateSelectChange}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer flex justify-end">
            <Button label="Cancel" color="white" onClick={onClose} />
            <Button
              label="Submit"
              color="green"
              loading={loading}
              onClick={handleSubmit}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

ReminderModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onClickOutside: PropTypes.func.isRequired,
};

export default ReminderModal;
